/**
 * paymentTaken.ts — "has this order's payment been taken?", for COPY ONLY.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescribing truth
 * (2026-09-12).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * READ THIS BEFORE USING IT ANYWHERE ELSE.
 *
 * Clinicians never see money, so no clinician read in this estate carries a
 * payment record — there is no `captured` field on `StaffOrder` and there must
 * not be one. What there IS is the order's own state, and the order only reaches
 * `approved` by way of a capture.
 *
 * So this is a HINT, and it is used for exactly one thing: choosing which true
 * sentence to show a clinician about money before they press a button. It is
 * NEVER a control. It does not enable or disable anything, it does not gate a
 * request, and no branch of it claims a payment WILL be taken — the correction
 * lane cannot charge in either branch, and the server enforces that with a guard
 * that throws, not with this function (R41: the UI is never the control).
 *
 * `null` is a first-class answer and the copy has a branch for it. A state this
 * function has not met is not "unpaid" — it is "this screen cannot tell", and
 * saying so is better than guessing about somebody's money.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * States that a case can only be in AFTER month 1 was captured. Approval is what
 * captures, and everything below is downstream of approval.
 */
const AFTER_CAPTURE = new Set(['approved', 'awaiting_dispense', 'dispensed', 'dispatched', 'dispense_rejected']);

/**
 * States that are definitively BEFORE any capture: the card may be authorised
 * (a hold), but nothing has been taken.
 */
const BEFORE_CAPTURE = new Set(['open', 'awaiting_payment', 'plan_selected', 'held', 'awaiting_approval', 'preapproved']);

/** true = taken, false = not yet, null = this screen cannot tell. */
export function paymentTakenForCopy(state: string | null | undefined): boolean | null {
  if (!state) return null;
  if (AFTER_CAPTURE.has(state)) return true;
  if (BEFORE_CAPTURE.has(state)) return false;
  // `rejected`, `cancelled`, `dormant`, `past_due` and anything a newer orders
  // revision adds: deliberately unknown rather than assumed either way.
  return null;
}
